import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { FileTextIcon, UsersIcon, MessageSquareIcon, SettingsIcon, PlusCircleIcon, LogOutIcon, HomeIcon, ChevronDownIcon, FolderIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ArticlesList from '../components/dashboard/ArticlesList';
import ArticleEditor from '../components/dashboard/ArticleEditor';
import UserManagement from '../components/dashboard/UserManagement';
import CommentModeration from '../components/dashboard/CommentModeration';
import Settings from '../components/dashboard/Settings';
import CategoryManager from '../components/dashboard/CategoryManager';
const DashboardPage: React.FC = () => {
  const {
    user,
    signOut,
    hasRole
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };
  if (!user) {
    return null; // Protected route should handle this
  }
  return <div className="w-full bg-lightGray min-h-screen" data-id="element-982">
      <div className="flex flex-col md:flex-row" data-id="element-983">
        {/* Sidebar - Desktop */}
        <div className="hidden md:block w-64 bg-navy text-white min-h-screen" data-id="element-984">
          <div className="p-4 border-b border-navy-800" data-id="element-985">
            <h2 className="text-xl font-bold font-montserrat" data-id="element-986">Dashboard</h2>
          </div>
          <div className="p-4" data-id="element-987">
            <div className="flex items-center mb-6" data-id="element-988">
              {user.photoURL ? <img src={user.photoURL} alt={user.displayName} className="w-10 h-10 rounded-full mr-3" data-id="element-989" /> : <div className="w-10 h-10 rounded-full bg-orange text-white flex items-center justify-center mr-3" data-id="element-990">
                  {user.displayName?.charAt(0).toUpperCase()}
                </div>}
              <div data-id="element-991">
                <div className="font-medium" data-id="element-992">{user.displayName}</div>
                <div className="text-xs text-gray-300 capitalize" data-id="element-993">
                  {user.role}
                </div>
              </div>
            </div>
            <nav className="space-y-1" data-id="element-994">
              <SidebarLink to="/dashboard" exact={true} icon={<HomeIcon size={18} data-id="element-996" />} label="Dashboard Home" data-id="element-995" />
              <div className="pt-4 pb-2" data-id="element-997">
                <h3 className="text-xs uppercase text-gray-400 font-semibold px-3" data-id="element-998">
                  Content Management
                </h3>
              </div>
              <SidebarLink to="/dashboard/articles" icon={<FileTextIcon size={18} data-id="element-1000" />} label="Articles" data-id="element-999" />
              <SidebarLink to="/dashboard/articles/new" icon={<PlusCircleIcon size={18} data-id="element-1002" />} label="New Article" data-id="element-1001" />
              <SidebarLink to="/dashboard/categories" icon={<FolderIcon size={18} data-id="element-1004" />} label="Categories" data-id="element-1003" />
              {hasRole(['superadmin', 'editor']) && <SidebarLink to="/dashboard/comments" icon={<MessageSquareIcon size={18} data-id="element-1006" />} label="Comment Moderation" data-id="element-1005" />}
              <div className="pt-4 pb-2" data-id="element-1007">
                <h3 className="text-xs uppercase text-gray-400 font-semibold px-3" data-id="element-1008">
                  Administration
                </h3>
              </div>
              {hasRole(['superadmin']) && <SidebarLink to="/dashboard/users" icon={<UsersIcon size={18} data-id="element-1010" />} label="User Management" data-id="element-1009" />}
              <SidebarLink to="/dashboard/settings" icon={<SettingsIcon size={18} data-id="element-1012" />} label="Settings" data-id="element-1011" />
            </nav>
            <div className="mt-8 pt-4 border-t border-navy-800" data-id="element-1013">
              <button onClick={handleSignOut} className="flex items-center text-gray-300 hover:text-white transition duration-200" data-id="element-1014">
                <LogOutIcon size={18} className="mr-2" data-id="element-1015" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Header */}
        <div className="md:hidden bg-navy text-white p-4" data-id="element-1016">
          <div className="flex justify-between items-center" data-id="element-1017">
            <h2 className="text-xl font-bold font-montserrat" data-id="element-1018">Dashboard</h2>
            <button onClick={() => setMenuOpen(!menuOpen)} className="text-white focus:outline-none" data-id="element-1019">
              <ChevronDownIcon size={24} className={`transform transition-transform ${menuOpen ? 'rotate-180' : ''}`} data-id="element-1020" />
            </button>
          </div>
          {menuOpen && <div className="mt-4" data-id="element-1021">
              <div className="flex items-center mb-4 pb-4 border-b border-navy-800" data-id="element-1022">
                {user.photoURL ? <img src={user.photoURL} alt={user.displayName} className="w-8 h-8 rounded-full mr-3" data-id="element-1023" /> : <div className="w-8 h-8 rounded-full bg-orange text-white flex items-center justify-center mr-3" data-id="element-1024">
                    {user.displayName?.charAt(0).toUpperCase()}
                  </div>}
                <div data-id="element-1025">
                  <div className="font-medium" data-id="element-1026">{user.displayName}</div>
                  <div className="text-xs text-gray-300 capitalize" data-id="element-1027">
                    {user.role}
                  </div>
                </div>
              </div>
              <nav className="space-y-3 mb-4" data-id="element-1028">
                <MobileSidebarLink to="/dashboard" exact={true} icon={<HomeIcon size={18} data-id="element-1030" />} label="Dashboard Home" onClick={() => setMenuOpen(false)} data-id="element-1029" />
                <div className="pt-2 pb-1" data-id="element-1031">
                  <h3 className="text-xs uppercase text-gray-400 font-semibold px-3" data-id="element-1032">
                    Content Management
                  </h3>
                </div>
                <MobileSidebarLink to="/dashboard/articles" icon={<FileTextIcon size={18} data-id="element-1034" />} label="Articles" onClick={() => setMenuOpen(false)} data-id="element-1033" />
                <MobileSidebarLink to="/dashboard/articles/new" icon={<PlusCircleIcon size={18} data-id="element-1036" />} label="New Article" onClick={() => setMenuOpen(false)} data-id="element-1035" />
                <MobileSidebarLink to="/dashboard/categories" icon={<FolderIcon size={18} data-id="element-1038" />} label="Categories" onClick={() => setMenuOpen(false)} data-id="element-1037" />
                {hasRole(['superadmin', 'editor']) && <MobileSidebarLink to="/dashboard/comments" icon={<MessageSquareIcon size={18} data-id="element-1040" />} label="Comment Moderation" onClick={() => setMenuOpen(false)} data-id="element-1039" />}
                <div className="pt-2 pb-1" data-id="element-1041">
                  <h3 className="text-xs uppercase text-gray-400 font-semibold px-3" data-id="element-1042">
                    Administration
                  </h3>
                </div>
                {hasRole(['superadmin']) && <MobileSidebarLink to="/dashboard/users" icon={<UsersIcon size={18} data-id="element-1044" />} label="User Management" onClick={() => setMenuOpen(false)} data-id="element-1043" />}
                <MobileSidebarLink to="/dashboard/settings" icon={<SettingsIcon size={18} data-id="element-1046" />} label="Settings" onClick={() => setMenuOpen(false)} data-id="element-1045" />
              </nav>
              <button onClick={handleSignOut} className="flex items-center text-gray-300 hover:text-white transition duration-200" data-id="element-1047">
                <LogOutIcon size={18} className="mr-2" data-id="element-1048" />
                Sign Out
              </button>
            </div>}
        </div>
        {/* Main Content */}
        <div className="flex-1" data-id="element-1049">
          <div className="p-6" data-id="element-1050">
            <Routes data-id="element-1051">
              <Route path="/" element={<DashboardHome data-id="element-1053" />} data-id="element-1052" />
              <Route path="/articles" element={<ArticlesList data-id="element-1055" />} data-id="element-1054" />
              <Route path="/articles/new" element={<ArticleEditor data-id="element-1057" />} data-id="element-1056" />
              <Route path="/articles/edit/:id" element={<ArticleEditor data-id="element-1059" />} data-id="element-1058" />
              <Route path="/categories" element={<CategoryManager data-id="element-1061" />} data-id="element-1060" />
              {hasRole(['superadmin']) && <Route path="/users" element={<UserManagement data-id="element-1063" />} data-id="element-1062" />}
              {hasRole(['superadmin', 'editor']) && <Route path="/comments" element={<CommentModeration data-id="element-1065" />} data-id="element-1064" />}
              <Route path="/settings" element={<Settings data-id="element-1067" />} data-id="element-1066" />
            </Routes>
          </div>
        </div>
      </div>
    </div>;
};
const DashboardHome: React.FC = () => {
  const {
    user
  } = useAuth();
  return <div data-id="element-1068">
      <h1 className="text-2xl font-bold text-navy mb-6 font-montserrat" data-id="element-1069">
        Welcome, {user?.displayName}
      </h1>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1070">
        <h2 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-1071">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1072">
          <Link to="/dashboard/articles/new" className="bg-orange text-white p-4 rounded-lg flex items-center justify-center hover:bg-orange-600 transition duration-200" data-id="element-1073">
            <PlusCircleIcon size={20} className="mr-2" data-id="element-1074" />
            Create New Article
          </Link>
          <Link to="/dashboard/articles" className="bg-navy text-white p-4 rounded-lg flex items-center justify-center hover:bg-navy-800 transition duration-200" data-id="element-1075">
            <FileTextIcon size={20} className="mr-2" data-id="element-1076" />
            Manage Articles
          </Link>
          <Link to="/enews" className="bg-gray-600 text-white p-4 rounded-lg flex items-center justify-center hover:bg-gray-700 transition duration-200" target="_blank" data-id="element-1077">
            <HomeIcon size={20} className="mr-2" data-id="element-1078" />
            View eNews
          </Link>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1079">
        <h2 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-1080">
          Dashboard Overview
        </h2>
        <p className="mb-4" data-id="element-1081">
          Welcome to the Operation Launchpad eNews dashboard. From here, you can
          manage articles, users, and comments based on your role permissions.
        </p>
        <ul className="list-disc pl-5 space-y-2" data-id="element-1082">
          <li data-id="element-1083">
            <strong data-id="element-1084">Authors:</strong> Create and edit your own articles
          </li>
          <li data-id="element-1085">
            <strong data-id="element-1086">Editors:</strong> Review, edit, and publish articles from
            all authors
          </li>
          <li data-id="element-1087">
            <strong data-id="element-1088">Contributors:</strong> Submit article drafts for review
          </li>
          <li data-id="element-1089">
            <strong data-id="element-1090">Super Admins:</strong> Manage users, roles, and all content
          </li>
        </ul>
      </div>
    </div>;
};
interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  exact?: boolean;
}
const SidebarLink: React.FC<SidebarLinkProps> = ({
  to,
  icon,
  label,
  exact
}) => {
  const location = useLocation();
  const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
  return <Link to={to} className={`flex items-center py-2 px-3 rounded-md transition duration-200 ${isActive ? 'bg-orange text-white' : 'text-gray-300 hover:bg-navy-800 hover:text-white'}`} data-id="element-1091">
      <span className="mr-3" data-id="element-1092">{icon}</span>
      {label}
    </Link>;
};
interface MobileSidebarLinkProps extends SidebarLinkProps {
  onClick: () => void;
}
const MobileSidebarLink: React.FC<MobileSidebarLinkProps> = ({
  to,
  icon,
  label,
  exact,
  onClick
}) => {
  const location = useLocation();
  const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
  return <Link to={to} className={`flex items-center py-2 px-3 rounded-md transition duration-200 ${isActive ? 'bg-orange text-white' : 'text-gray-300 hover:bg-navy-800 hover:text-white'}`} onClick={onClick} data-id="element-1093">
      <span className="mr-3" data-id="element-1094">{icon}</span>
      {label}
    </Link>;
};
export default DashboardPage;